import React, { useState, useMemo } from 'react'
import {
  Avatar,
  AvatarBadge,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Text,
  Tooltip,
  Badge,
  useColorModeValue,
  HStack,
  Tag,
  TagLabel,
  IconButton,
  useDisclosure,
  Collapse,
  Button,
  Image,
} from '@chakra-ui/react'
import { firstLetterUpperCaseAndRestLowerCase } from '../../../utils/capitalFirstLetter'
import { format, formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { MaisDetalhes } from './MaisDetalhes'
import { FinalizarChamado } from './FinalizarChamado'
import { ChevronDownIcon, ChevronUpIcon, InfoIcon, AttachmentIcon } from '@chakra-ui/icons'
import { ImageViewer } from './ImageViewer'

export const Card = React.memo(
  ({
    item,
    setPriorityColor,
    updatePriorityChamado,
    listPriority,
    priority,
    isDragging,
    isAdminView = false, // Nova prop para identificar se está na visão de admin
  }) => {
    const { isOpen, onToggle } = useDisclosure()
    const { isOpen: isDetailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure()

    const cardBg = useColorModeValue('white', 'gray.700')
    const borderColor = useColorModeValue('gray.200', 'gray.600')
    const textColor = useColorModeValue('gray.700', 'gray.200')
    const secondaryTextColor = useColorModeValue('gray.500', 'gray.400')

    const [isHovered, setIsHovered] = useState(false)

    // Memoizar cálculos de datas para evitar re-cálculos desnecessários
    const dateInfo = useMemo(() => {
      const abreventDate = new Date(item.data_abertura)
      return {
        abreventDate,
        timeAgo: formatDistanceToNow(abreventDate, { locale: ptBR, addSuffix: true }),
        formattedDate: format(abreventDate, 'PPPp', { locale: ptBR }),
      }
    }, [item.data_abertura])

    // Determinar o valor inicial do select baseado no responsavel (memoizado)
    const responsavelValue = useMemo(() => {
      return item.responsavel === 'FRONT' ? 'F' : item.responsavel === 'BACK' ? 'BE' : item.responsavel === 'BANCO' ? 'B' : ''
    }, [item.responsavel])

    // Memoizar o callback de mudança no select
    const handleSelectChange = React.useCallback(
      (e) => {
        if (updatePriorityChamado) {
          updatePriorityChamado({
            usuario_prioridade: e.target.value,
            id_chamado: item.numero_chamado,
          })
        }
      },
      [updatePriorityChamado, item.numero_chamado]
    )

    return (
      <Box
        bg={cardBg}
        borderRadius="md"
        p="4" // Aumentando o padding interno
        boxShadow={isDragging ? 'lg' : isHovered ? 'md' : 'sm'}
        position="relative"
        w="100%"
        borderLeftWidth="4px"
        borderLeftColor={setPriorityColor(item.prioridade)}
        transition="all 0.2s ease"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        transform={isDragging ? 'scale(1.02)' : isHovered ? 'scale(1.01)' : 'scale(1)'}
        cursor={isAdminView ? 'grab' : 'default'}
        _active={isAdminView ? { cursor: 'grabbing' } : {}}
        opacity={isDragging ? 0.8 : 1} // Dá uma dica visual durante o drag
      >
        <CardHeader item={item} />

        <CardBody
          item={item}
          dateInfo={dateInfo}
          textColor={textColor}
          secondaryTextColor={secondaryTextColor}
          setPriorityColor={setPriorityColor}
          onOpenDetails={onDetailsOpen} // Passamos a função para abrir o modal
        />

        {/* Adiciona a visualização da imagem se existir */}
        {item.link_imagem && (
          <Box mt={3} mb={2}>
            <ImageViewer imageUrl={item.link_imagem} alt={`Captura de tela do chamado #${item.numero_chamado}`} />
          </Box>
        )}

        <CardFooter
          isOpen={isOpen}
          onToggle={onToggle}
          item={item}
          borderColor={borderColor}
          isDetailsOpen={isDetailsOpen}
          onDetailsOpen={onDetailsOpen}
          onDetailsClose={onDetailsClose}
          hasImage={Boolean(item.link_imagem)}
          isAdminView={isAdminView}
        />

        {isAdminView && (
          <Collapse in={isOpen} animateOpacity>
            <Box pt={3} pb={1}>
              <FormControl id={`prioridade-${item.numero_chamado}`} size="sm">
                <FormLabel fontSize="xs">Responsável</FormLabel>
                <Select onChange={handleSelectChange} defaultValue={responsavelValue} id={`prioridade-select-${item.numero_chamado}`} size="sm">
                  <option value="">Selecione</option>
                  {listPriority &&
                    listPriority.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.name}
                      </option>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </Collapse>
        )}

        {/* Modal de detalhes é renderizado uma vez aqui e pode ser aberto por diferentes componentes */}
        <MaisDetalhes item={item} externalIsOpen={isDetailsOpen} externalOnOpen={onDetailsOpen} externalOnClose={onDetailsClose} />
      </Box>
    )
  }
)

// Componentes menores para melhor performance e componentização
const CardHeader = React.memo(({ item }) => {
  const getColorScheme = (responsavel) => {
    switch (responsavel) {
      case 'FRONT':
        return 'blue'
      case 'BACK':
        return 'purple'
      case 'BANCO':
        return 'orange'
      default:
        return 'gray'
    }
  }

  return (
    <Flex justifyContent="space-between" alignItems="center" mb={2}>
      <Badge colorScheme="blue" fontSize="0.7em">
        #{item?.numero_chamado}
      </Badge>

      {item.responsavel && (
        <Tag size="sm" borderRadius="full" variant="solid" colorScheme={getColorScheme(item.responsavel)}>
          <TagLabel>{item.responsavel}</TagLabel>
        </Tag>
      )}
    </Flex>
  )
})

// Modificamos o CardBody para tornar a descrição clicável
const CardBody = React.memo(({ item, dateInfo, textColor, secondaryTextColor, setPriorityColor, onOpenDetails }) => {
  const getPriorityColorScheme = (prioridade) => {
    if (prioridade === 'Alta' || prioridade === 'Muito alta') return 'red'
    if (prioridade === 'Media') return 'yellow'
    return 'green'
  }

  // Extrair um preview da descrição
  const descriptionPreview = useMemo(() => {
    const description = item.detalhes_problema || ''
    return description.length > 100 ? `${description.substring(0, 100)}...` : description
  }, [item.detalhes_problema])

  return (
    <>
      <Box mb={3}>
        <Flex alignItems="center" mb={2}>
          <Avatar size="sm" name={item.solicitante} mr={2}>
            <AvatarBadge boxSize="1em" bg={setPriorityColor(item.prioridade)} />
          </Avatar>
          <Text fontWeight="semibold" fontSize="sm" color={textColor} noOfLines={1}>
            {firstLetterUpperCaseAndRestLowerCase(item.solicitante)}
          </Text>
        </Flex>

        <Text fontSize="sm" fontWeight="medium" mb={1} noOfLines={1}>
          {item.titulo || `Chamado Nº ${item.numero_chamado}`}
        </Text>

        {/* Preview da descrição do problema - agora clicável */}
        {item.detalhes_problema && (
          <Box
            bg="gray.50"
            p={2}
            borderRadius="md"
            fontSize="xs"
            mt={2}
            mb={2}
            color="gray.700"
            borderLeft="3px solid"
            borderLeftColor="blue.400"
            maxH="60px"
            overflowY="auto"
            sx={{
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'blue.100',
                borderRadius: '24px',
              },
            }}
            onClick={onOpenDetails} // Adicionamos o evento de clique
            cursor="pointer" // Indicação visual de que é clicável
            _hover={{
              bg: 'gray.100',
              transition: 'all 0.2s',
            }} // Feedback visual ao passar o mouse
            role="button"
            aria-label="Ver detalhes completos"
          >
            <Tooltip label="Clique para ver os detalhes completos">
              <Text noOfLines={3}>{descriptionPreview}</Text>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <HStack spacing={1}>
          <Tag size="sm" colorScheme={getPriorityColorScheme(item.prioridade)}>
            {item.prioridade}
          </Tag>
          <Tag size="sm" colorScheme="blue">
            {item.tipo_chamado}
          </Tag>
          {item.link_imagem && (
            <Tag size="sm" colorScheme="green" variant="outline">
              <AttachmentIcon mr={1} />
              Print
            </Tag>
          )}
        </HStack>

        <Tooltip label={dateInfo.formattedDate}>
          <Text fontSize="xs" color={secondaryTextColor}>
            {dateInfo.timeAgo}
          </Text>
        </Tooltip>
      </Flex>
    </>
  )
})

// Modificado para usar o estado de abertura do modal do componente pai e adicionar controle condicional baseado na view
const CardFooter = React.memo(({ isOpen, onToggle, item, borderColor, isDetailsOpen, onDetailsOpen, hasImage, isAdminView }) => {
  return (
    <Flex justifyContent="space-between" alignItems="center" borderTopWidth="1px" borderColor={borderColor} pt={2}>
      {isAdminView && (
        <IconButton
          icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          size="sm"
          variant="ghost"
          onClick={onToggle}
          aria-label={isOpen ? 'Recolher' : 'Expandir'}
        />
      )}
      <Button
        size="sm"
        colorScheme="blue"
        variant="outline"
        leftIcon={<InfoIcon />}
        fontSize="xs"
        onClick={onDetailsOpen}
        ml={!isAdminView ? 'auto' : 0}
        mr={!isAdminView ? 'auto' : 0}
      >
        Detalhes
      </Button>
    </Flex>
  )
})
